import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Outlet } from "react-router-dom";

import { FacilityProvider, useFacilitySync } from "@/app/FacilityContext";
import { useSelectedOrganization } from "@/app/OrganizationContext";
import { BusinessShell } from "@/app/navigation/BusinessShell";
import { useMyOrg } from "@/hooks/use-my-org";
import type { OrgWorkspace } from "@/libs/onboardingApi";

/** Mount the existing facility-selection reconciliation once under the shell. */
function FacilitySyncMount() {
  useFacilitySync();
  return null;
}

function OrganizationSelectionPage({
  organizations,
}: {
  organizations: OrgWorkspace[];
}) {
  const { selectOrganization } = useSelectedOrganization();

  return (
    <Box
      component="main"
      id="main-content"
      tabIndex={-1}
      sx={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        bgcolor: "background.default",
        px: 2,
        py: 5,
      }}
    >
      <Paper
        component="section"
        aria-labelledby="workspace-heading"
        variant="outlined"
        sx={{ width: "100%", maxWidth: 560, overflow: "hidden" }}
      >
        <Stack spacing={1.25} sx={{ px: 3, py: 2.5 }}>
          <ApartmentOutlinedIcon color="primary" aria-hidden="true" />
          <Typography id="workspace-heading" component="h1" variant="h5">
            Select organization
          </Typography>
        </Stack>
        <Divider />
        <List disablePadding>
          {organizations.map((organization) => (
            <ListItem key={organization.org_id} disablePadding>
              <ListItemButton
                onClick={() => selectOrganization(organization.org_id)}
                sx={{ px: 3, py: 2 }}
              >
                <ListItemText
                  primary={organization.business_name}
                  secondary={organization.status.replace(/_/g, " ")}
                  slotProps={{
                    primary: { sx: { fontWeight: 700 } },
                    secondary: { sx: { textTransform: "capitalize" } },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Paper>
    </Box>
  );
}

function DashboardShell() {
  return (
    <BusinessShell>
      <Outlet />
    </BusinessShell>
  );
}

function TenantAwareDashboard() {
  const view = useMyOrg();
  if (view.kind === "selection-required") {
    return <OrganizationSelectionPage organizations={view.organizations} />;
  }

  return (
    <FacilityProvider>
      <FacilitySyncMount />
      <DashboardShell />
    </FacilityProvider>
  );
}

export function DashboardLayout() {
  return <TenantAwareDashboard />;
}
